import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { del, get, post } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { dateOnly, humanize, money } from '@/lib/utils';
import { DataTable, Dialog } from '@/components/ui/layout';
import { Alert, Badge, Button, Card, CardContent, CardHeader, CardTitle, DatePicker, Field, Textarea } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';

interface Rule {
  id: string;
  name: string;
  isActive: boolean;
  bedClass: string | null;
  ward: { id: string; name: string } | null;
  chargeItem: { code: string; name: string; basePriceMinor: number };
}

interface Pause {
  id: string;
  ruleId: string;
  ruleName: string;
  fromDate: string;
  toDate: string | null;
  reason: string | null;
}

export function WardChargePanel({ admissionId, admitted }: { admissionId: string; admitted: boolean }) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [pausing, setPausing] = useState<Rule | null>(null);

  const rules = useQuery({ queryKey: ['ward-charge-rules'], queryFn: () => get<Rule[]>('/ipd/ward-charge-rules') });
  const pauses = useQuery({
    queryKey: ['ward-charge-pauses', admissionId],
    queryFn: () => get<Pause[]>(`/ipd/admissions/${admissionId}/ward-charge-pauses`),
  });

  const resume = useMutation({
    mutationFn: (pauseId: string) => del(`/ipd/admissions/${admissionId}/ward-charge-pauses/${pauseId}`),
    onSuccess: () => {
      toast.success(tr('ipd', 'charge_resumed'), tr('ipd', 'it_is_posted_again_from_today'));
      void queryClient.invalidateQueries({ queryKey: ['ward-charge-pauses', admissionId] });
    },
    onError: (error) => toast.error(tr('ipd', 'could_not_resume_the_charge'), describeError(error)),
  });

  const paused = new Set(
    (pauses.data ?? []).filter((pause) => pause.toDate === null || pause.toDate >= new Date().toISOString().slice(0, 10)).map((pause) => pause.ruleId),
  );
  const manage = can('ward.charge.manage') && admitted;
  const active = (rules.data ?? []).filter((rule) => rule.isActive);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">{tr('ipd', 'recurring_daily_charges')}</CardTitle>
        </CardHeader>
        <CardContent>
          <DataTable
            rows={active}
            loading={rules.isLoading} refreshing={rules.isFetching}
            error={rules.error}
            rowKey={(row) => row.id}
            empty={tr('ipd', 'the_hospital_has_no_recurring_ward_charges_set')}
            dense
            columns={[
              {
                header: tr('ipd', 'charge'),
                sort: (row) => row.name,
                cell: (row) => (
                  <div>
                    <p className="font-medium">{row.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {row.chargeItem.code} · {money(row.chargeItem.basePriceMinor)} {tr('ipd', 'a_day')}{' '}
                      {row.ward || row.bedClass ? [row.ward?.name, row.bedClass ? `${humanize(row.bedClass)} beds` : null].filter(Boolean).join(' · ') : tr('ipd', 'every_patient')}
                    </p>
                  </div>
                ),
              },
              {
                header: tr('ipd', 'for_this_patient'),
                sort: (row) => paused.has(row.id),
                cell: (row) => (paused.has(row.id) ? <Badge variant="warning">{tr('ipd', 'paused')}</Badge> : <Badge variant="success">{tr('ipd', 'charged_daily')}</Badge>),
              },
              {
                header: '',
                align: 'right',
                cell: (row) =>
                  manage &&
                  !paused.has(row.id) && (
                    <Button size="sm" variant="outline" onClick={() => setPausing(row)}>
                      {tr('ipd', 'pause_for_this_patient')}
                    </Button>
                  ),
              },
            ]}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">{tr('ipd', 'pauses_on_this_admission')}</CardTitle>
        </CardHeader>
        <CardContent>
          <DataTable
            rows={pauses.data}
            loading={pauses.isLoading} refreshing={pauses.isFetching}
            error={pauses.error}
            rowKey={(row) => row.id}
            empty={tr('ipd', 'no_charge_has_been_paused_for_this_patient')}
            dense
            columns={[
              { header: tr('ipd', 'charge'), sort: (row) => row.ruleName, cell: (row) => row.ruleName },
              { header: tr('ipd', 'from'), sort: (row) => row.fromDate, cell: (row) => dateOnly(row.fromDate) },
              { header: tr('ipd', 'until'), sort: (row) => row.toDate, cell: (row) => (row.toDate ? dateOnly(row.toDate) : 'Until resumed') },
              { header: tr('ipd', 'why'), sort: (row) => row.reason, cell: (row) => row.reason ?? '—' },
              {
                header: '',
                align: 'right',
                cell: (row) =>
                  manage &&
                  (row.toDate === null || row.toDate >= new Date().toISOString().slice(0, 10)) && (
                    <Button size="sm" variant="ghost" onClick={() => resume.mutate(row.id)}>
                      {tr('ipd', 'resume')}
                    </Button>
                  ),
              },
            ]}
          />
        </CardContent>
      </Card>

      {pausing && (
        <PauseDialog
          admissionId={admissionId}
          rule={pausing}
          onClose={() => setPausing(null)}
          onSaved={() => {
            setPausing(null);
            void queryClient.invalidateQueries({ queryKey: ['ward-charge-pauses', admissionId] });
          }}
        />
      )}
    </div>
  );
}

function PauseDialog({ admissionId, rule, onClose, onSaved }: { admissionId: string; rule: Rule; onClose: () => void; onSaved: () => void }) {
  const toast = useToast();
  const [reason, setReason] = useState('');
  const [until, setUntil] = useState('');
  const [error, setError] = useState<string | null>(null);

  const pause = useMutation({
    mutationFn: () => post(`/ipd/admissions/${admissionId}/ward-charge-pauses`, { ruleId: rule.id, reason, toDate: until || null }),
    onSuccess: () => {
      toast.success(tr('ipd', 'charge_paused'), tr('ipd', 'x_is_not_posted_for_this_patient_until_it_is', { name: rule.name }));
      onSaved();
    },
    onError: (caught) => setError(describeError(caught)),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={tr('ipd', 'pause_x', { name: rule.name })}
      description={tr('ipd', 'days_already_posted_stay_on_the_bill_use_the')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {tr('ipd', 'cancel')}
          </Button>
          <Button
            onClick={() => {
              setError(null);
              pause.mutate();
            }}
            loading={pause.isPending}
            disabled={reason.trim().length < 3}
          >
            {tr('ipd', 'pause')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label={tr('ipd', 'why')} required>
          <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder={tr('ipd', 'patient_is_in_theatre_no_visit_charge_today')} />
        </Field>
        <Field label={tr('ipd', 'until')} hint={tr('ipd', 'leave_blank_to_pause_until_someone_resumes_it')}>
          <DatePicker value={until} onChange={setUntil} />
        </Field>
        {error && <Alert variant="destructive">{error}</Alert>}
      </div>
    </Dialog>
  );
}
